"use client";

import { faCheck } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { type ComponentProps, Fragment, useDeferredValue, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import useSWR from "swr";
import {
	Button,
	Dialog,
	FilterButton,
	NativeInput,
	Typography,
	cn,
	formatLocale,
	formatNumber,
	getConnection,
	getThumbnail,
	offsetToCursor,
} from "../..";
import type { FrontendQuery, PublicEdgeBand } from "./../../../../apps/frontend/pimcore";

export type EdgeBand = PublicEdgeBand;

interface EdgeBandComponentProps extends ComponentProps<"button"> {
	sku: string;
	suspense?: boolean;
	selectedBand?: EdgeBand;
	setSelectedBand: (band?: EdgeBand) => void;
	onApplyCallback?: (band?: EdgeBand) => void;
	dialogMessage?: string;
	size?: "small" | "default";
}

export function EdgeBandComponent(props: EdgeBandComponentProps) {
	const {
		sku,
		suspense,
		selectedBand,
		setSelectedBand,
		onApplyCallback,
		dialogMessage,
		size = "default",
		className,
		...rest
	} = props;
	const intl = useIntl();

	const { data, error, isLoading, pageSize, page, setPage, setFilter, search, setSearch } =
		useEdgeBandsData({ sku, suspense });

	const nodes = getConnection(data?.edgeBandsQuery.available);

	const [open, setOpen] = useState(false);

	function onOpenChange(open: boolean) {
		setOpen(open);
		setSearch("");
		setFilter([]);
		setPage(1);
	}

	function onPageChange(variant: "next" | "prev") {
		setPage((prev) => (variant === "next" ? prev + 1 : prev - 1));

		const element = document.getElementById("available-bands");
		element?.scrollIntoView({ behavior: "smooth", block: "start", inline: "nearest" });
	}

	const onApply = (band?: EdgeBand) => {
		setSelectedBand(band);
		onApplyCallback?.(band);
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		// if no band is selected and no band is applied to the workpiece, apply the first band
		if (!(isLoading || selectedBand)) {
			onApply();
		}
	}, [isLoading, selectedBand]);

	return (
		<>
			<button
				onClick={() => setOpen(!open)}
				type="button"
				className={cn(
					"selectArrow flex w-full items-center gap-2 rounded border border-shades px-4 py-2 pr-8 text-left disabled:cursor-not-allowed",
					className,
				)}
				{...rest}
			>
				{selectedBand?.thickness ? (
					<>
						{selectedBand.image && (
							<img
								src={getThumbnail(selectedBand.image, intl.locale)}
								alt={selectedBand.name}
								width={size === "small" ? 24 : 48}
								height={size === "small" ? 24 : 48}
								className={cn("shrink-0 rounded object-cover", {
									"h-6 w-6": size === "small",
									"h-12 w-12": size === "default",
								})}
							/>
						)}

						<span>
							{selectedBand.name}{" "}
							{formatNumber({
								lang: intl.locale,
								value: selectedBand.thickness,
								unit: "millimeter",
								digits: { min: 0, max: 0 },
							})}
						</span>

						{!!selectedBand.price && (
							<span className="shrink-0 text-shades-medium">
								(
								{formatNumber({
									lang: intl.locale,
									value: Number(selectedBand.price),
									style: "currency",
								})}
								)
							</span>
						)}
					</>
				) : (
					intl.formatMessage({ defaultMessage: "Kies jouw gewenste band", id: "BkfGnv" })
				)}
			</button>

			<Dialog
				width="xl"
				open={open}
				onOpenChange={onOpenChange}
				title={intl.formatMessage({ defaultMessage: "Kies jouw gewenste band", id: "BkfGnv" })}
			>
				<div className="flex flex-col gap-4">
					{/* {!!finish.options.custom?.constraints.start_fee?.excludingTax && (
						<Typography>
							{intl.formatMessage(
								{
									defaultMessage:
										"Dit band heeft een starttarief van {value} per combinatie van houtsoort en dikte in je winkelwagen",
									id: "ucLBR0",
								},
								{
									value: formatNumber({
										lang: intl.locale,
										value: getMoneyValue(finish.options.custom.constraints.start_fee),
										style: "currency",
									}),
								},
							)}
						</Typography>
					)} */}

					{dialogMessage && <Typography>{dialogMessage}</Typography>}

					{!!data?.edgeBandsQuery?.matching.length && (
						<>
							<div className="flex flex-col gap-1">
								<Typography variant="paragraph-bold">
									{intl.formatMessage({ defaultMessage: "Passend bij de houtsoort", id: "V06IHB" })}
								</Typography>

								<div className="flex flex-col gap-2">
									{data.edgeBandsQuery.matching.map((band) => (
										<EdgeBandItem
											key={band.id}
											band={band}
											selected={band.id === selectedBand?.id}
											size={size}
											onClick={() => {
												onApply(band);
												onOpenChange(false);
											}}
										/>
									))}
								</div>
							</div>

							<hr className="border-shades" />
						</>
					)}

					<div className="flex flex-col gap-1" id="available-bands">
						<Typography variant="paragraph-bold">
							{intl.formatMessage({
								defaultMessage: "Alle banden in ons assortiment",
								id: "JOWujp",
							})}
						</Typography>

						<NativeInput
							autoFocus
							placeholder={intl.formatMessage({
								defaultMessage: "Zoek op bandnaam of kleur",
								id: "+8JONj",
							})}
							type="text"
							value={search}
							onChange={(e) => {
								setSearch(e.target.value);
								setPage(1);
							}}
							onKeyDown={(e) => e.stopPropagation()}
						/>
					</div>

					{!!data?.edgeBandsQuery.aggregations.length && (
						<div className="scrollbar-none flex snap-x gap-4 overflow-x-scroll">
							{data.edgeBandsQuery.aggregations.map((aggregation) => (
								<FilterButton
									key={aggregation.value}
									aggregation={{
										...aggregation,
										options: aggregation.options.map(({ value, label }) => ({
											value,
											label: !Number.isNaN(Number(value))
												? formatNumber({
														lang: intl.locale,
														value: Number(value),
														unit: "millimeter",
														digits: { min: 0, max: 1 },
													})
												: label,
										})),
									}}
									searchMessage={intl.formatMessage({
										defaultMessage: "Zoeken naar",
										id: "txYckZ",
									})}
									emptyMessage={intl.formatMessage({
										defaultMessage: "Geen resultaten gevonden",
										id: "WaivPi",
									})}
									onOptionChange={setFilter}
								/>
							))}
						</div>
					)}

					{error ? (
						<div className="text-danger">
							{error instanceof Error
								? error.message
								: intl.formatMessage({ defaultMessage: "Er is iets misgegaan", id: "vv11HN" })}
						</div>
					) : isLoading ? (
						<>
							<div className="h-6 w-full animate-pulse rounded bg-shades-light" />

							<div className="flex flex-col gap-2">
								{Array.from({ length: pageSize }).map((_, index) => (
									<div
										key={index}
										className="h-16 animate-pulse rounded bg-shades-light odd:bg-shades-medium-light"
									/>
								))}
							</div>

							<div className="h-6 w-full animate-pulse rounded bg-shades-light sm:hidden" />
						</>
					) : data?.edgeBandsQuery.available.edges?.length ? (
						<Fragment>
							<div className="flex items-center justify-between gap-4">
								<span>
									{intl.formatMessage(
										{ defaultMessage: "{value} resultaten", id: "scd2ww" },
										{
											value: `${data.edgeBandsQuery.available.edges.length * page}/${data.edgeBandsQuery.available.totalCount}`,
										},
									)}
								</span>

								<Pagination
									currentPage={page}
									hasMorePages={data.edgeBandsQuery.available.pageInfo.hasNextPage}
									onPageChange={onPageChange}
									className="hidden sm:flex"
								/>
							</div>

							<div className="flex flex-col gap-2">
								{nodes.map((band) => (
									<EdgeBandItem
										key={band.id}
										band={band}
										selected={band.id === selectedBand?.id}
										size={size}
										onClick={() => {
											onApply(band);
											onOpenChange(false);
										}}
									/>
								))}
							</div>

							<Pagination
								currentPage={page}
								hasMorePages={data.edgeBandsQuery.available.pageInfo.hasNextPage}
								onPageChange={onPageChange}
								className="flex sm:hidden"
							/>
						</Fragment>
					) : (
						<div className="min-h-[calc(24px+1rem+424px)]">
							{intl.formatMessage({ defaultMessage: "Geen resultaten gevonden", id: "WaivPi" })}
						</div>
					)}
				</div>
			</Dialog>
		</>
	);
}

interface EdgeBandItemProps {
	band: EdgeBand;
	selected: boolean;
	onClick: () => void;
	size?: "small" | "default";
}

function EdgeBandItem({ band, selected, onClick, size = "default" }: EdgeBandItemProps) {
	const intl = useIntl();

	return (
		<button
			type="button"
			key={band.id}
			className={cn(
				"group relative flex appearance-none items-center justify-between gap-4 rounded p-2 odd:bg-shades-light enter:hover:bg-primary-light focus:outline-none motion-safe:transition-colors",
				{ "ring-2 ring-success ring-inset": selected },
			)}
			onClick={() => onClick()}
		>
			<div className="flex items-center gap-4">
				{!!band.image && (
					<img
						src={
							typeof band.image === "object" ? getThumbnail(band.image, intl.locale) : band.image
						}
						alt={band.name}
						width={size === "small" ? 24 : 48}
						height={size === "small" ? 24 : 48}
						className={cn("shrink-0 rounded object-cover", {
							"h-6 w-6": size === "small",
							"h-12 w-12": size === "default",
						})}
					/>
				)}

				<div className="flex flex-col items-start text-left">
					<div className="font-bold">{band.name}</div>
					<div className="flex gap-4">
						{!!band.thickness && (
							<div>
								<span className="text-shades-dark">
									{intl.formatMessage({ defaultMessage: "Dikte", id: "qzkByd" })}:{" "}
								</span>
								{formatNumber({
									lang: intl.locale,
									value: band.thickness,
									unit: "millimeter",
									digits: { min: 0, max: 1 },
								})}
							</div>
						)}

						{!!band.minOrderLength && band.minOrderLength > 1 && (
							<div>
								<span className="text-shades-dark">
									{intl.formatMessage({ defaultMessage: "Minimale afname", id: "9ggn+B" })}:{" "}
								</span>
								{formatNumber({
									lang: intl.locale,
									value: band.minOrderLength,
									unit: "meter",
									digits: { min: 0, max: 0 },
								})}
							</div>
						)}
					</div>
				</div>
			</div>

			{!!band.price && (
				<div className="shrink-0 text-sm">
					{`${formatNumber({
						lang: intl.locale,
						value: typeof band.price === "object" ? getMoneyValue(band.price) : Number(band.price),
						style: "decimal",
						digits: { min: 2, max: 2 },
					})} p/m1`}
				</div>
			)}

			<div
				className={cn(
					"-right-2 -top-2 absolute h-4 w-4 items-center justify-center rounded-full bg-success text-[10px] text-white leading-none",
					{ hidden: !selected, flex: selected },
				)}
			>
				<FontAwesomeIcon icon={faCheck} />
			</div>
		</button>
	);
}

interface PaginationProps {
	currentPage: number;
	hasMorePages: boolean;
	onPageChange: (variant: "next" | "prev") => void;
	className?: string;
}

function Pagination({ currentPage, hasMorePages, onPageChange, className }: PaginationProps) {
	const intl = useIntl();

	return (
		<div className={cn(className, "justify-center gap-4")}>
			<Button variant="link" onClick={() => onPageChange("prev")} disabled={currentPage <= 1}>
				{intl.formatMessage({ defaultMessage: "Vorige", id: "5FuA/J" })}
			</Button>

			<Button variant="link" onClick={() => onPageChange("next")} disabled={!hasMorePages}>
				{intl.formatMessage({ defaultMessage: "Volgende", id: "13MUCY" })}
			</Button>
		</div>
	);
}

interface EdgeBandsDataProps {
	sku: string;
	suspense?: boolean;
}

export function useEdgeBandsData({ sku, suspense }: EdgeBandsDataProps) {
	const pageSize = 6;
	const intl = useIntl();
	const [page, setPage] = useState(1);
	const [search, setSearch] = useState<string>("");
	const [filter, setFilter] = useState<{ value: string; options: string[] }[]>([]);
	const query = useDeferredValue(search);

	const swr = useSWR<Pick<FrontendQuery, "edgeBandsQuery">, Error>(
		[
			edgeBandsQuery,
			{
				sku,
				defaultLanguage: formatLocale(intl.locale, "language_COUNTRY"),
				query,
				filter,
				first: pageSize,
				after: page > 1 ? offsetToCursor((page - 1) * pageSize) : undefined,
			},
		],
		{ keepPreviousData: true, fetcher: pimcoreFetcher, suspense },
	);

	return {
		...swr,
		pageSize,
		page,
		setPage,
		filter,
		setFilter,
		search,
		setSearch,
	};
}

export const pimcoreFetcher = async ([query, variables]: [unknown, unknown]) => {
	const input = process.env.NEXT_PUBLIC_PIMCORE_API_URL;

	if (input === undefined) {
		throw new Error("No Pimcore API url found");
	}

	const response = await fetch(input, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ query, variables }),
	});

	const json = (await response.json()) as { data: never };

	return json.data;
};

export const edgeBandsQuery = /* GraphQL */ `
  query EdgeBandsQuery(
    $sku: String!
    $defaultLanguage: String!
    $query: String
    $filter: [AggregationInput!]
    $after: String
    $first: Int
    $before: String
    $last: Int
  ) {
    edgeBandsQuery(
      sku: $sku
      defaultLanguage: $defaultLanguage
      query: $query
      filter: $filter
      after: $after
      first: $first
      before: $before
      last: $last
    ) {
      aggregations {
        label
        value
        options {
          value
          label
          count
        }
      }
      available {
        pageInfo {
          hasNextPage
        }
        totalCount
        edges {
          node {
            ...PublicEdgeBand
          }
        }
      }
      matching {
        ...PublicEdgeBand
      }
    }
  }

  fragment PublicEdgeBand on PublicEdgeBand {
    id
    parentId
    sku
    name
    thickness
    width
    image {
      id
      filename
      fullpath
    }
    minOrderLength
    price
  }
`;

export const edgeBandQuery = /* GraphQL */ `
  query EdgeBandQuery($sku: String!, $defaultLanguage: String!) {
    edgeBandQuery(sku: $sku, defaultLanguage: $defaultLanguage) {
      id
      parentId
      sku
      name
      thickness
      width
      image {
        id
        filename
        fullpath
      }
      minOrderLength
      price
    }
  }
`;

/**
 * @param display
 * 1 = excluding tax,
 * 2 = including tax,
 * 3 = including tax and including tax
 */
export type Money = {
	currency: string;
	display: 1 | 2 | 3 | PriceDisplay;
	includingTax: number;
	excludingTax: number;
};

enum PriceDisplay {
	Both = "BOTH",
	ExcludingTax = "EXCLUDING_TAX",
	IncludingTax = "INCLUDING_TAX",
}

export function getMoneyValue(money: Money) {
	const isDisplay =
		typeof money.display === "number"
			? money.display === 2
			: money.display === PriceDisplay.IncludingTax;

	return isDisplay ? money.includingTax : money.excludingTax;
}
